import { AnalysisStatus } from '../../../common/entities/analysis-status.entity';
import { RiskLevel } from '../../../common/entities/risk-level.entity';
import { AnalysisDetail } from '../../application/entities/analysis-detail.entity';
import { TaskStatus, EvidenceType } from '../../application/entities/analysis-task.entity';
import { AnalysisDetailResponseDto } from '../dto/analysis-detail-response.dto';

export const toAnalysisDetail = (dto: AnalysisDetailResponseDto): AnalysisDetail => {
  const d = dto.data;
  const progress = d.tasksTotal > 0
    ? Math.round((d.tasksCompleted / d.tasksTotal) * 100)
    : 0;

  const sortedLocationPath = [...d.locationPath].sort((a, b) => a.displayOrder - b.displayOrder);

  return {
    id: d.uuid,
    title: d.title,
    model: d.model,
    currentPl: d.currentPl ?? 'e',
    requiredPl: d.requiredPl ?? 'e',
    riskLevel: (d.riskLevel ?? null) as RiskLevel | null,
    averageHrn: d.averageHrn ?? null,
    status: d.status as AnalysisStatus,
    plantUuid: sortedLocationPath.at(-1)?.locationUuid ?? null,
    locationPath: sortedLocationPath.map(l => ({ levelName: l.levelName, locationName: l.locationName })),
    consultant: d.consultant ?? '',
    client: d.client,
    updatedAt: d.updatedAt,
    imageUrl: d.imageUrl ?? '',
    pdfUrl: d.pdfUrl,
    excelUrl: d.excelUrl,
    reportUrl: d.reportUrl,
    tasksCompleted: d.tasksCompleted,
    tasksTotal: d.tasksTotal,
    progressPercent: progress,
    tasks: [...d.risks].sort((a, b) => a.hazardCode.localeCompare(b.hazardCode, undefined, { numeric: true, sensitivity: 'base' })).map((risk) => {
      const taskProgress = risk.requirementsTotal > 0
        ? Math.round((risk.requirementsCompleted / risk.requirementsTotal) * 100)
        : 100;
      return {
        id: risk.uuid,
        hazardCode: risk.hazardCode,
        hazardType: risk.hazardType,
        description: risk.description,
        modeOrStep: risk.modeOrStep,
        accessPoint: risk.accessPoint ?? null,
        plrCategory: risk.plrCategory,
        initialAssessment: risk.initialAssessment,
        finalAssessment: risk.finalAssessment,
        riskReductionPercent: risk.reductionPercent,
        securityFunction: risk.securityFunction ?? null,
        correctiveMeasures: risk.requirements.map(req => ({
          id: req.uuid,
          reference: req.reference,
          number: req.number,
          securityFunctionCode: req.securityFunctionCode,
          description: req.description,
          additionalObservations: req.additionalObservations ?? null,
          existingMeasures: req.existingMeasures,
          status: req.status as TaskStatus,
          assignedToUuid: req.assignedToUuid ?? null,
          assignedToName: req.assignedToName ?? null,
          managerUuid: req.managerUuid ?? null,
          managerName: req.managerName ?? null,
          evidence: (req.evidence ?? []).map(ev => ({
            id: ev.uuid,
            type: (ev.type === 'image' ? 'file' : ev.type) as EvidenceType,
            content: ev.content ?? null,
            url: ev.url ?? null,
            filename: ev.filename ?? null,
            createdAt: ev.createdAt,
            createdBy: ev.createdBy,
          })),
        })),
        status: risk.status,
        tasksCompleted: risk.requirementsCompleted,
        tasksTotal: risk.requirementsTotal,
        progressPercent: taskProgress,
        riskLevel: risk.riskLevel as RiskLevel,
        imageUrl: risk.imageUrl,
        images: risk.images?.map(img => ({ id: img.id, imageUrl: img.imageUrl, displayOrder: img.displayOrder })) ?? [],
      };
    }),
    findings: d.findings.map((f) => ({
      id: f.uuid,
      description: f.description,
      imageRef: f.imageRef ?? null,
      type: f.type ?? null,
      imageUrl: f.imageUrl ?? null,
    })),
    importWarnings: d.importWarnings ?? [],
  };
};
